"use client";

import { Checkbox, SelectField } from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  HINT,
  INPUT,
  LABEL,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { codeColourFor } from "@/components/tools/qualitative-text/coding-colours";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  codePath,
  codeTree,
  type Excerpt,
  type Project,
} from "@/lib/tool-kit/qualitative-text/coding";

export const EXCERPT_LIMIT = 200;

interface ExcerptStrings {
  excerptsHeading: string;
  filterLabel: string;
  allCodes: string;
  includeChildren: string;
  noExcerpts: string;
  excerptsTruncated: string;
  memoLabel: string;
  removeExcerpt: string;
  location: string;
}

export function CodingExcerpts({
  project,
  rows,
  filter,
  includeChildren,
  strings,
  onFilter,
  onIncludeChildren,
  onMemo,
  onRemove,
}: {
  project: Project;
  rows: readonly Excerpt[];
  filter: string;
  includeChildren: boolean;
  strings: ExcerptStrings;
  onFilter: (value: string) => void;
  onIncludeChildren: (value: boolean) => void;
  onMemo: (segmentId: string, memo: string) => void;
  onRemove: (segmentId: string) => void;
}) {
  const fmt = useToolNumber();
  return (
    <div className="space-y-4">
      <h3 className={WIDGET_H3}>{strings.excerptsHeading}</h3>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="qc-filter"
          label={strings.filterLabel}
          onChange={onFilter}
          options={[
            { value: "", label: strings.allCodes },
            ...codeTree(project).map(({ code }) => ({
              value: code.id,
              label: codePath(project, code.id),
            })),
          ]}
          value={filter}
        />
        <div className="flex items-end pb-2">
          <Checkbox
            checked={includeChildren}
            label={strings.includeChildren}
            onChange={onIncludeChildren}
          />
        </div>
      </div>
      {rows.length === 0 ? (
        <p className={HINT}>{strings.noExcerpts}</p>
      ) : (
        <ul className="space-y-3">
          {rows.slice(0, EXCERPT_LIMIT).map((row) => (
            <li
              className="space-y-2 rounded-lg border border-border bg-card p-3"
              key={row.segment.id}
            >
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span
                  aria-hidden="true"
                  className={`h-3 w-3 rounded-full ${codeColourFor(project, row.segment.codeId).swatch}`}
                />
                <span className="font-medium text-foreground">{row.path}</span>
                <span className="text-muted-foreground">
                  {t(strings.location, {
                    document: row.document.title,
                    start: fmt(row.segment.start + 1),
                    end: fmt(row.segment.end),
                  })}
                </span>
              </div>
              <blockquote className="border-border border-l-2 pl-3 text-foreground text-sm">
                {row.text}
              </blockquote>
              <div className="flex flex-wrap items-center gap-2">
                <label className={LABEL} htmlFor={`qc-memo-${row.segment.id}`}>
                  {strings.memoLabel}
                </label>
                <input
                  className={`${INPUT} flex-1`}
                  id={`qc-memo-${row.segment.id}`}
                  onChange={(event) =>
                    onMemo(row.segment.id, event.target.value)
                  }
                  value={row.segment.memo}
                />
                <button
                  className={BUTTON_SMALL}
                  onClick={() => onRemove(row.segment.id)}
                  type="button"
                >
                  {strings.removeExcerpt}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {rows.length > EXCERPT_LIMIT ? (
        <p className={HINT}>
          {t(strings.excerptsTruncated, {
            shown: fmt(EXCERPT_LIMIT),
            total: fmt(rows.length),
          })}
        </p>
      ) : null}
    </div>
  );
}
